/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import React, { useCallback, useContext, useRef, useState, useEffect } from 'react';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled â€“ go/akcss
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent';
import { PageCommentEditor } from '@confluence/comment-simple/entry-points/PageCommentEditor';
import { type CommentSaveHandler } from '@confluence/comment-simple/entry-points/commentSimpleTypes';
import { fg } from '@confluence/feature-gating';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component';
import {
	startEditorSubExperience,
	startEditorExperiences,
} from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import {
	CommentsType,
	type GraphQLContentStatus,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralCommentMutation';
import { updateApolloCacheParentCallback } from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState';
import {
	CommentType,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex/entry-points/NavdexPointType';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel/entry-points/commentThreadHelper';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics';
import { LoadablePaint } from '@confluence/loadable';

import { useDraftCommentBox } from '../hooks/useDraftCommentBox';

import type {
	ObjectAddCommentContainerLegacyUserQuery as ObjectAddCommentContainerLegacyUserQuery$data,
	ObjectAddCommentContainerLegacyUserQueryVariables as ObjectAddCommentContainerLegacyUserQuery$variables,
} from './__types__/ObjectAddCommentContainerLegacyUserQuery';
import type {
	ObjectAddCommentContainerLegacyCreateGeneralCommentMutation as ObjectAddCommentContainerLegacyCreateGeneralCommentMutation$data,
	ObjectAddCommentContainerLegacyCreateGeneralCommentMutationVariables as ObjectAddCommentContainerLegacyCreateGeneralCommentMutation$variables,
} from './__types__/ObjectAddCommentContainerLegacyCreateGeneralCommentMutation';
import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions';
import { DraftCommentBox } from './DraftCommentBox';

const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const ObjectAddCommentContainerLegacyCreateGeneralCommentMutation = gql`
	mutation ObjectAddCommentContainerLegacyCreateGeneralCommentMutation(
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;
const ObjectAddCommentContainerLegacyUserQuery = gql`
	query ObjectAddCommentContainerLegacyUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

// TODO: remove when cleaning up `confluence_frontend_content_wrapper`
const oldAddCommentContainerStyles = xcss({
	position: 'sticky',
	bottom: 'space.0',
	display: 'flex',
	width: '100%',
	padding: 'space.200',
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	backgroundColor: 'color.background.input',
});

const addCommentContainerStyles = xcss({
	position: 'sticky',
	bottom: 'space.0',
	display: 'flex',
	width: '100%',
	padding: 'space.200',
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface',
});

const addCommentClosedContainerStyles = xcss({
	width: '100%',
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	flexShrink: 0,
});

const addCommentOpenContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	width: '100%',
	gap: 'space.200',
	alignItems: 'flex-start',
});

const addCommentEditorStyles = xcss({
	width: '100%',
});

const avatarStyles = xcss({
	width: '32px',
	height: '32px',
	float: 'left',
});

const hideStyle = xcss({
	display: 'none',
});

const addCommentReactionsContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});

export const ObjectAddCommentContainerLegacy = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}) => {
	const [isEditorEmpty, setIsEditorEmpty] = useState<boolean>(true);

	const { userId, edition } = useSessionData();
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer, closeCommentContainer } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();

	// Use the custom hook for draft box logic
	const { shouldShowDraftBox, hideDraftBox } = useDraftCommentBox({
		expanded,
		isEditorEmpty,
	});

	// Monitor expanded state changes
	useEffect(() => {
		// Experience metric will be added here
	}, [expanded, shouldShowDraftBox]);

	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const [createGeneralCommentFn] = useMutation<
		ObjectAddCommentContainerLegacyCreateGeneralCommentMutation$data,
		ObjectAddCommentContainerLegacyCreateGeneralCommentMutation$variables
	>(ObjectAddCommentContainerLegacyCreateGeneralCommentMutation);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const pageMode = useGetPageMode();
	const [{ searchSessionId }] = useSearchSessionId();
	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const ObjectAddCommentContainerLegacyId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		if (!fg('confluence_fe_add_page_comment_exp_coverage')) {
			startEditorExperiences(
				{
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					attributes: {
						editor: 'FABRIC',
						isSingleExperience: true,
						edition,
						source: AnalyticsSource.PAGE_FOOTER,
					},
				},
				{
					name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
				},
				{
					name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		}

		expandCommentContainer({ entryPoint: 'pageFooterLegacy' });
	}, [edition, expandCommentContainer]);

	const handleCommentsPanelSave: CommentSaveHandler = (adfObject: any, onSuccess: any) => {
		const pageId = contentId || '';
		const { adf } = adfObject;

		startEditorSubExperience({
			name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});

		return createGeneralCommentFn({
			variables: {
				cloudId,
				input: {
					commentBody: {
						value: JSON.stringify(adf),
						representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
					},
					commentSource: Platform.WEB,
					containerId: pageId,
				},
			},
			// @ts-ignore -- can't import type directly in update FN because it'd be circular
			update: updateApolloCacheParentCallback(
				'create',
				{
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
					type: [CommentsType.FOOTER],
				},
				isReactionsEnabled,
			),
		}).then(({ data }: any) => {
			const commentResult = data.createFooterComment;
			const comment: CommentData = {
				...commentResult,
				replies: [], // not included in the payload
				isUnread: false,
				isOpen: true,
				type: CommentType.GENERAL,
			};

			updateReadCommentsListState((prev) => new Set([...prev, comment.id]));
			addNewCommentThreads({ inline: {}, general: { [comment.id]: comment } });

			onSuccess && onSuccess();
			resetContentChanged();
			closeCommentContainer();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: comment.id, // The newly created comment ID
					objectType: contentType,
					objectId: contentId,
					source: AnalyticsSource.PAGE_FOOTER,
					attributes: {
						commentType: AnalyticsCommentType.PAGE,
						source: AnalyticsSource.PAGE_FOOTER,
						pageMode,
						isReply: false,
						pageType: contentType,
						isLivePage: pageMode === PageMode.LIVE,
						editor: 'v2',
						searchSessionId,
						navdexPointType: END,
						parentCommentId: null, // analytics event schema type expects string or null
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			});

			setTimeout(() => {
				handlePanelSelectionAndScroll({
					threadKey: comment.id,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: undefined,
					commentType: CommentType.GENERAL,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
				});
			}, 1000);
		});
	};

	const handleCancel = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
		const doCancel = () => {
			if (isEditorAborted) {
				shouldAutofocusPromptRef.current = true;
				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason: 'aborted because comment was cancelled',
				});
			}

			closeCommentContainer();
		};

		if (hasContentChanged) {
			resetContentChanged();
		}
		doCancel();
	};

	const handleOnEditorLoaded = () => {
		experienceTracker.succeed({
			name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
		});

		const footerCommentContainer = document.getElementById(ObjectAddCommentContainerLegacyId);

		if (!footerCommentContainer) {
			return;
		}

		footerCommentContainer.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'nearest',
		});
	};

	const handleEditorContentChanged = (content: any) => {
		setIsEditorEmpty(!content);
		// Also call the original onChange handler
		onChange(content);
	};
	const { data: userData } = useQuery<
		ObjectAddCommentContainerLegacyUserQuery$data,
		ObjectAddCommentContainerLegacyUserQuery$variables
	>(ObjectAddCommentContainerLegacyUserQuery, {
		variables: { id: userId || '' },
		fetchPolicy: 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: !userId,
	});
	const canViewProfile = true;

	const userInfo = {
		avatarUrl: userData?.user?.photos?.[0]?.value || '',
		accountId: userId,
		displayName: userData?.user?.displayName || '',
	};

	return (
		<>
			<Box
				id={ObjectAddCommentContainerLegacyId}
				xcss={[
					fg('confluence_frontend_content_wrapper')
						? addCommentContainerStyles
						: oldAddCommentContainerStyles,
				]}
				testId={ObjectAddCommentContainerLegacyId}
			>
				{!expanded ? (
					<Box xcss={addCommentClosedContainerStyles}>
						<ObjectAddCommentPrompt
							onClick={handleExpandCommentContainer}
							autofocusRef={shouldAutofocusPromptRef}
						/>
						<Flex gap="space.050">
							{showLabels && fg('confluence_frontend_labels_in_footer') && (
								<>
									<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
									<FooterLabelsLoader />
								</>
							)}
							{showReactions && (
								<Box xcss={addCommentReactionsContainerStyles}>
									<ObjectCommentPageReactions
										isOffline={isOffline}
										optimisticReactions={optimisticReactions}
									/>
								</Box>
							)}
						</Flex>
					</Box>
				) : (
					<Box xcss={addCommentOpenContainerStyles}>
						<Box xcss={avatarStyles}>
							<ProfileAvatar
								canViewProfile={canViewProfile}
								src={userInfo.avatarUrl}
								userId={userInfo.accountId}
								isAnonymous={!userInfo.accountId}
								name={userInfo.displayName}
								testId="profile-avatar-comment"
							/>
						</Box>
						{expanded && shouldShowDraftBox && (
							<DraftCommentBox expandPageCommentEditorHandler={hideDraftBox} />
						)}
						<Box xcss={[addCommentEditorStyles, shouldShowDraftBox && hideStyle]}>
							<PageCommentEditor
								mode="create"
								saveHandler={handleCommentsPanelSave}
								cancelHandler={handleCancel}
								onEditorLoaded={handleOnEditorLoaded}
								onEditorContentChanged={handleEditorContentChanged}
								useNewExperienceAbort
								expandEditor
								isFooterComment
								isOffline={isOffline}
							/>
						</Box>
					</Box>
				)}
			</Box>
		</>
	);
};
